import { useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

/**
 * Shared "Re-tag this recording" modal. Mounted on all three call
 * detail surfaces (Demo, Qualifying Call, Meeting).
 *
 * Usage:
 *   const [open, setOpen] = useState(false);
 *   <ReclassifyModal
 *     open={open}
 *     recordingId={call.id}
 *     currentKind={call.recordingKind}
 *     authToken={authToken}
 *     onClose={() => setOpen(false)}
 *     onReclassified={(result) => { ... refresh data ... }}
 *   />
 *
 * Idempotent on the backend: re-tagging to the current kind is a
 * no-op. Backend pipeline kicker handles the heavy work async; we
 * surface a toast-friendly hint via onReclassified(result.kickedPipeline).
 */

const KIND_OPTIONS = [
  {
    id: 'demo',
    label: 'Demo',
    description: "Product walkthrough you'd send to a prospect. Generates a trailer (~2-3 min) if one doesn't already exist.",
  },
  {
    id: 'qualifying_call',
    label: 'Qualifying Call',
    description: 'Discovery / intro call. Generates an AI summary structured around pain points, criteria, and next steps.',
  },
  {
    id: 'meeting',
    label: 'Meeting',
    description: 'Internal sync, customer-success check-in, partner call. Generates a generic AI summary (key takeaways + action items).',
  },
];

export default function ReclassifyModal({
  open,
  recordingId,
  currentKind,
  authToken,
  onClose,
  onReclassified,
}) {
  const [picked, setPicked] = useState(currentKind || 'meeting');
  const [keepOriginal, setKeepOriginal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Re-seed the selection every time the modal OPENS (or is re-pointed
  // at a different recording). The list surfaces mount ONE shared
  // instance with currentKind=null while closed, so the useState
  // initializer alone pre-picked 'meeting' regardless of the card's
  // real kind — with the submit button live, one accidental click
  // re-tagged the recording and kicked summary regeneration.
  useEffect(() => {
    if (open) {
      setPicked(currentKind || 'meeting');
      setKeepOriginal(false);
      setError(null);
    }
  }, [open, recordingId, currentKind]);

  if (!open) return null;

  const submit = async () => {
    if (!recordingId || !picked) return;
    if (picked === currentKind) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/recordings/${encodeURIComponent(recordingId)}/recording-kind`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ kind: picked, keepOriginal }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `Re-tag failed (${r.status})`);
      onClose();
      if (onReclassified) onReclassified(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--room-card)', borderRadius: 16, padding: 28,
          maxWidth: 540, width: '100%',
          boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
          maxHeight: 'calc(100vh - 48px)', overflow: 'auto',
        }}
      >
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--room-text-strong)' }}>
            Re-tag this recording
          </h2>
          <p style={{ margin: '6px 0 0', color: 'var(--room-text-muted)', fontSize: 14, lineHeight: 1.5 }}>
            Mislabelled at the time it was scheduled? Pick the right type and we'll generate the matching artifacts in the background. Existing trailers + summaries are kept either way — re-tagging only ADDS, it never destroys work.
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {KIND_OPTIONS.map((opt) => {
            const active = picked === opt.id;
            const isCurrent = currentKind === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setPicked(opt.id)}
                disabled={busy}
                style={{
                  textAlign: 'left',
                  padding: 14,
                  borderRadius: 12,
                  border: `2px solid ${active ? '#0891b2' : 'var(--room-border, #e2e8f0)'}`,
                  // Active state is a semantic cyan PAIR (stays hardcoded);
                  // the inactive arm must follow the theme card surface.
                  background: active ? '#ecfeff' : 'var(--room-card, #fff)',
                  cursor: busy ? 'wait' : 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.12s ease',
                  position: 'relative',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  {/* Active card is a self-consistent light pair (pale cyan bg
                      + dark cyan ink) in BOTH themes; inactive follows tokens. */}
                  <strong style={{ fontSize: 15, color: active ? '#164e63' : 'var(--room-text-strong)' }}>{opt.label}</strong>
                  {isCurrent && (
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: '#0891b2',
                      background: '#cffafe', padding: '2px 8px', borderRadius: 100,
                      letterSpacing: '0.04em', textTransform: 'uppercase',
                    }}>
                      Current
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: active ? '#155e75' : 'var(--room-text-body, #475569)', lineHeight: 1.5 }}>
                  {opt.description}
                </div>
              </button>
            );
          })}
        </div>

        {/* Keep-both option (2026-08-13): duplicates the recording into
            a second card of the picked kind — e.g. one recording living
            as BOTH a Demo (trailer) and a Meeting (summary). Only shown
            once the user has picked a different kind. */}
        {picked !== currentKind && currentKind && (
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '12px 14px', borderRadius: 12, marginBottom: 16,
            border: '1px dashed var(--room-border, #e2e8f0)',
            cursor: busy ? 'wait' : 'pointer',
          }}>
            <input
              type="checkbox"
              checked={keepOriginal}
              disabled={busy}
              onChange={(e) => setKeepOriginal(e.target.checked)}
              style={{ marginTop: 3, accentColor: '#0891b2' }}
            />
            <span style={{ fontSize: 13, color: 'var(--room-text-body, #475569)', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--room-text-strong)', fontWeight: 600 }}>
                Keep the {KIND_OPTIONS.find((o) => o.id === currentKind)?.label || currentKind} version too
              </strong>
              <br />
              The recording will exist as both — this card stays as it is, and a second
              {' '}{KIND_OPTIONS.find((o) => o.id === picked)?.label || picked} card is created
              from the same recording with its own artifacts.
            </span>
          </label>
        )}

        {error && (
          <div style={{
            padding: '10px 12px', borderRadius: 8, marginBottom: 12,
            background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#dc2626', fontSize: 13,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              padding: '10px 18px', borderRadius: 10,
              border: '1px solid var(--room-border)', background: 'var(--room-card)',
              color: 'var(--room-text-body, #475569)', fontFamily: 'inherit', fontSize: 14, fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || picked === currentKind}
            style={{
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #0891b2 0%, #0ea5e9 50%, #6366f1 100%)',
              color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
              cursor: busy || picked === currentKind ? 'wait' : 'pointer',
              opacity: busy || picked === currentKind ? 0.7 : 1,
            }}
          >
            {busy
              ? (keepOriginal ? 'Duplicating…' : 'Re-tagging…')
              : picked === currentKind
              ? 'No change'
              : keepOriginal
              ? `Add ${KIND_OPTIONS.find((o) => o.id === picked)?.label || picked} copy`
              : `Re-tag as ${KIND_OPTIONS.find((o) => o.id === picked)?.label || picked}`}
          </button>
        </div>
      </div>
    </div>
  );
}
